import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Recycle,
  ChefHat,
  Clock,
  Sparkles,
  Plus,
  ArrowRight,
  Package,
  Loader2,
  Lightbulb
} from 'lucide-react';

const LeftoversControl = () => {
  const { api, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [leftovers, setLeftovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIngredient, setExpandedIngredient] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [loadingSuggestions, setLoadingSuggestions] = useState({});

  useEffect(() => {
    if (isAuthenticated) {
      loadLeftovers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadLeftovers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/cart/leftovers');
      setLeftovers(res.data.leftovers || []);
    } catch (err) {
      console.error('Failed to load leftovers:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestionsForIngredient = async (ingredient, amount, unit) => {
    if (suggestions[ingredient]) return;
    
    try {
      setLoadingSuggestions(prev => ({ ...prev, [ingredient]: true }));
      const res = await api.post('/cart/use-leftover', null, {
        params: { ingredient, amount, unit }
      });
      setSuggestions(prev => ({ ...prev, [ingredient]: res.data.suggestions || [] }));
    } catch (err) {
      toast.error('Failed to get suggestions');
    } finally {
      setLoadingSuggestions(prev => ({ ...prev, [ingredient]: false }));
    }
  };

  const handleExpand = (ingredient, amount, unit) => {
    if (expandedIngredient === ingredient) {
      setExpandedIngredient(null);
    } else {
      setExpandedIngredient(ingredient);
      loadSuggestionsForIngredient(ingredient, amount, unit);
    }
  };

  const addMealToPlan = async (meal) => {
    try {
      // Navigate to meal planner with the meal suggestion
      toast.success(`"${meal.meal_name}" added to your plan!`);
      navigate('/meal-planner');
    } catch (err) {
      toast.error('Failed to add meal');
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <Card data-testid="leftovers-loading">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="leftovers-control">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Recycle className="w-5 h-5 text-green-600" />
          <CardTitle className="text-lg">Use Your Leftovers</CardTitle>
        </div>
        <CardDescription>
          These ingredients will be left over after your planned meals. Find recipes to use them up!
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {leftovers.length === 0 ? (
          <div className="text-center py-6">
            <Package className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">
              No leftovers detected! Your grocery list is perfectly matched to your meal plan.
            </p>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
              <Lightbulb className="w-4 h-4 text-green-600" />
              <p className="text-sm text-green-800">
                {leftovers.length} ingredient{leftovers.length !== 1 ? 's' : ''} will have leftovers. 
                Tap to see meal suggestions!
              </p>
            </div>

            {/* Leftover items */}
            <div className="space-y-3">
              {leftovers.map((item) => (
                <div 
                  key={item.ingredient}
                  className="border rounded-lg overflow-hidden"
                  data-testid={`leftover-item-${item.ingredient.replace(/\s+/g, '-')}`}
                >
                  {/* Leftover header */}
                  <button
                    className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
                    onClick={() => handleExpand(item.ingredient, item.leftover_amount, item.unit)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Package className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium capitalize">{item.ingredient}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.leftover_amount} {item.unit} left over
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {Math.round((item.leftover_amount / item.purchased_amount) * 100)}% extra
                      </Badge>
                      <ArrowRight 
                        className={`w-4 h-4 text-muted-foreground transition-transform ${
                          expandedIngredient === item.ingredient ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {/* Expanded suggestions */}
                  {expandedIngredient === item.ingredient && (
                    <div className="border-t bg-muted/30 p-3 space-y-3">
                      {/* Storage tip */}
                      {item.storage_tip && (
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Sparkles className="w-4 h-4 text-primary mt-0.5" />
                          <span>{item.storage_tip}</span>
                        </div>
                      )}

                      {/* Meal suggestions */}
                      {loadingSuggestions[item.ingredient] ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        </div>
                      ) : suggestions[item.ingredient]?.length > 0 ? (
                        <div className="space-y-2">
                          <p className="text-sm font-medium">Meals that use {item.ingredient}:</p>
                          {suggestions[item.ingredient].slice(0, 3).map((meal, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center justify-between p-2 bg-background rounded-md"
                            >
                              <div className="flex items-center gap-2">
                                <ChefHat className="w-4 h-4 text-primary" />
                                <span className="text-sm">{meal.meal_name}</span>
                                {meal.perfect_fit && (
                                  <Badge variant="secondary" className="text-xs">Perfect fit</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {meal.prep_time && (
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {meal.prep_time}m
                                  </span>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => addMealToPlan(meal)}
                                  data-testid={`add-meal-${meal.meal_id}`}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-2">
                          No meal suggestions found for this ingredient
                        </p>
                      )}

                      {/* Quick suggestions from the API response */}
                      {item.can_use_in?.length > 0 && !suggestions[item.ingredient] && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium">Quick suggestions:</p>
                          {item.can_use_in.map((meal, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center justify-between p-2 bg-background rounded-md"
                            >
                              <div className="flex items-center gap-2">
                                <ChefHat className="w-4 h-4 text-primary" />
                                <span className="text-sm">{meal.meal_name}</span>
                              </div>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => addMealToPlan(meal)}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LeftoversControl;
